// CardWallets.tsx - Fintech-grade redesign with Test/Live mode toggle
import React, { useEffect, useMemo, useState } from "react";
import {
  CreditCard,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  Shield,
  Wifi,
  Download,
  MoreVertical,
  Info,
  AlertCircle,
  TestTube2,
  Zap,
  TrendingUp,
  Lock,
  CheckCircle2,
} from "lucide-react";
import { cardService, CardWallet } from "../services/cardService";
import { useLocalization } from "../contexts/LocalizationContext";
import LoadingScreen from "./LoadingScreen";

type WalletMode = "test" | "production";

const MODE_LABELS: Record<WalletMode, string> = {
  test: "Test",
  production: "Live",
};

const CardWallets: React.FC = () => {
  const { formatCurrency } = useLocalization();

  const [wallets, setWallets] = useState<CardWallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeMode, setActiveMode] = useState<WalletMode>("production");
  const [showFullNumbers, setShowFullNumbers] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  // Persist selected mode across reloads
  useEffect(() => {
    const saved = localStorage.getItem("moko.cards.mode");
    if (saved === "test" || saved === "production") {
      setActiveMode(saved);
    }
  }, []);

  const setMode = (mode: WalletMode) => {
    setActiveMode(mode);
    localStorage.setItem("moko.cards.mode", mode);
  };

  // Wallets filtered by currently selected mode
  const visibleWallets = useMemo(
    () => wallets.filter((w) => w.mode === activeMode),
    [wallets, activeMode]
  );

  const usdWallet = visibleWallets.find((w) => w.currency === "USD");
  const cdfWallet = visibleWallets.find((w) => w.currency === "CDF");

  // Count wallets per mode (to show on the toggle)
  const countByMode = useMemo(() => {
    const counts: Record<WalletMode, number> = { test: 0, production: 0 };
    wallets.forEach((w) => {
      if (w.mode === "test" || w.mode === "production") counts[w.mode]++;
    });
    return counts;
  }, [wallets]);

  const showToast = (message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchWallets = async () => {
    try {
      setError(null);
      setLoading(true);
      const response = await cardService.getWalletBalances();
      setWallets(response.wallets || []);
    } catch (err: any) {
      console.error("Error fetching wallets:", err);
      setError(err?.response?.data?.detail || "Failed to load wallet balances");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCardNumber = (cardNumber: string) => {
    navigator.clipboard
      .writeText(cardNumber)
      .then(() => showToast("Card number copied to clipboard"))
      .catch(() => showToast("Failed to copy", "error"));
  };

  const toggleShowNumber = (walletId: string) => {
    setShowFullNumbers((prev) => ({ ...prev, [walletId]: !prev[walletId] }));
  };

  useEffect(() => {
    fetchWallets();
  }, []);

  // Theme tokens based on active mode
  const isTest = activeMode === "test";
  const theme = {
    accent: isTest ? "amber" : "emerald",
    accentHex: isTest ? "#f59e0b" : "#10b981",
    bgSoft: isTest ? "bg-amber-50" : "bg-emerald-50",
    textAccent: isTest ? "text-amber-700" : "text-emerald-700",
    borderAccent: isTest ? "border-amber-200" : "border-emerald-200",
  };

  // Loading state
  if (loading) {
    return <LoadingScreen fullscreen={false} message="Loading your virtual cards..." />;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-6 right-6 z-50 px-4 py-3 rounded-xl shadow-xl border backdrop-blur-sm transform transition-all duration-300 ${
            toast.type === "success"
              ? "bg-white text-slate-900 border-slate-200"
              : "bg-red-50 text-red-800 border-red-200"
          }`}
        >
          <div className="flex items-center gap-2.5">
            {toast.type === "success" ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600" />
            )}
            <span className="text-sm font-medium">{toast.message}</span>
          </div>
        </div>
      )}

        {/* Slim toolbar — title is rendered by the parent /balances wrapper */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center p-0.5 rounded-lg bg-gray-100 border border-gray-200">
              <button
                onClick={() => setMode("production")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                  activeMode === "production" ? "bg-white text-emerald-700 shadow-sm" : "text-gray-500 hover:text-gray-800"
                }`}
              >
                <Zap className="w-3.5 h-3.5" /> Live
                {countByMode.production > 0 && (
                  <span className={`text-[10px] px-1 py-px rounded ${activeMode === "production" ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"}`}>{countByMode.production}</span>
                )}
              </button>
              <button
                onClick={() => setMode("test")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                  activeMode === "test" ? "bg-white text-amber-700 shadow-sm" : "text-gray-500 hover:text-gray-800"
                }`}
              >
                <TestTube2 className="w-3.5 h-3.5" /> Test
                {countByMode.test > 0 && (
                  <span className={`text-[10px] px-1 py-px rounded ${activeMode === "test" ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-600"}`}>{countByMode.test}</span>
                )}
              </button>
            </div>
            <button
              onClick={fetchWallets}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors text-xs font-medium"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors text-xs font-medium">
              <Download className="w-3.5 h-3.5" /> Export
            </button>
          </div>
        </div>

        {/* Stat row — light cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{MODE_LABELS[activeMode]} Wallets</span>
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${theme.bgSoft} ${theme.textAccent}`}>{MODE_LABELS[activeMode].toUpperCase()}</span>
            </div>
            <div className="mt-1 text-xl font-bold tabular-nums text-gray-900">{visibleWallets.length}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">USD Balance</span>
              <span className="w-5 h-5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-bold flex items-center justify-center">$</span>
            </div>
            <div className="mt-1 text-xl font-bold tabular-nums text-gray-900">
              {usdWallet ? formatCurrency(usdWallet.balance, "USD") : "$0.00"}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">CDF Balance</span>
              <span className="w-5 h-5 rounded bg-teal-50 text-teal-700 text-[9px] font-bold flex items-center justify-center">FC</span>
            </div>
            <div className="mt-1 text-xl font-bold tabular-nums text-gray-900">
              {cdfWallet ? formatCurrency(cdfWallet.balance, "CDF") : "FC 0"}
            </div>
          </div>
        </div>

        {/* Test Mode Banner */}
        {isTest && (
          <div className="mb-6 p-4 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 flex items-start gap-3">
            <div className="p-1.5 rounded-lg bg-amber-100">
              <TestTube2 className="w-4 h-4 text-amber-700" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-amber-900">
                You are viewing Test mode data
              </div>
              <div className="text-sm text-amber-800 mt-0.5">
                Test balances and transactions are isolated from your live
                environment. No real money is involved.
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">{error}</p>
              <button
                onClick={fetchWallets}
                className="mt-1 text-sm text-red-600 hover:text-red-800 font-semibold"
              >
                Try again →
              </button>
            </div>
          </div>
        )}

        {/* Cards grid */}
        {visibleWallets.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 shadow-sm">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 flex items-center justify-center">
              <CreditCard className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              No {MODE_LABELS[activeMode]} cards yet
            </h3>
            <p className="text-slate-600 max-w-md mx-auto mb-6">
              {isTest
                ? "You don't have any test virtual cards. Contact support to enable test mode."
                : "Your live virtual cards will appear here once activated."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-10">
            {visibleWallets.map((wallet) => {
              const displayNumber = showFullNumbers[wallet.id]
                ? cardService.formatCardNumber(wallet.virtual_card_number)
                : cardService.maskCardNumber(wallet.virtual_card_number);

              const isUsd = wallet.currency === "USD";

              // Card visual theme based on currency AND mode
              const cardBg = isTest
                ? "bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600"
                : isUsd
                ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
                : "bg-gradient-to-br from-emerald-700 via-emerald-800 to-emerald-900";

              return (
                <div
                  key={wallet.id}
                  className="group relative overflow-hidden rounded-xl bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  {/* Compact card visual */}
                  <div className={`relative ${cardBg} px-4 py-3.5 text-white overflow-hidden`}>
                    <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/5"></div>

                    {/* Top row: badges + menu */}
                    <div className="relative flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1.5">
                        <span className="px-2 py-0.5 rounded bg-white/15 backdrop-blur-sm text-[9px] font-bold tracking-widest uppercase">
                          {isTest ? "Test" : "Live"}
                        </span>
                        <span className="px-2 py-0.5 rounded bg-white/15 backdrop-blur-sm text-[9px] font-bold tracking-wider">
                          {wallet.currency}
                        </span>
                      </div>
                      <button className="p-1 rounded hover:bg-white/15 transition-colors">
                        <MoreVertical className="w-3.5 h-3.5 text-white/80" />
                      </button>
                    </div>

                    {/* Chip + contactless */}
                    <div className="relative flex items-center gap-2 mb-3">
                      <div className="w-7 h-5 rounded bg-gradient-to-br from-yellow-200 to-yellow-400 flex items-center justify-center">
                        <div className="w-4 h-3 border border-yellow-700/30 rounded-sm"></div>
                      </div>
                      <Wifi className="w-3.5 h-3.5 text-white/70 rotate-90" />
                    </div>

                    {/* Card number — single row with action icons */}
                    <div className="relative flex items-center justify-between mb-3">
                      <span className="font-mono text-sm font-semibold tracking-[0.16em] tabular-nums">
                        {displayNumber}
                      </span>
                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={() => toggleShowNumber(wallet.id.toString())}
                          className="p-1 rounded hover:bg-white/15 transition-colors"
                          title={showFullNumbers[wallet.id] ? "Hide" : "Show"}
                        >
                          {showFullNumbers[wallet.id] ? (
                            <EyeOff className="w-3 h-3 text-white/80" />
                          ) : (
                            <Eye className="w-3 h-3 text-white/80" />
                          )}
                        </button>
                        <button
                          onClick={() => handleCopyCardNumber(wallet.virtual_card_number)}
                          className="p-1 rounded hover:bg-white/15 transition-colors"
                          title="Copy"
                        >
                          <Copy className="w-3 h-3 text-white/80" />
                        </button>
                      </div>
                    </div>

                    {/* Balance + network */}
                    <div className="relative flex items-end justify-between">
                      <div>
                        <div className="text-[9px] uppercase tracking-widest text-white/60">Balance</div>
                        <div className="text-lg font-bold tabular-nums leading-none mt-0.5">
                          {formatCurrency(wallet.balance, wallet.currency)}
                        </div>
                      </div>
                      <div className="text-[10px] font-bold italic text-white/80">MOKO</div>
                    </div>
                  </div>

                  {/* Info footer */}
                  <div className="px-4 py-2 bg-white flex items-center justify-between border-t border-gray-100">
                    <div className="flex items-center gap-3 text-[10px] text-gray-500">
                      <span className="inline-flex items-center gap-1"><Shield className="w-3 h-3" /> Secure</span>
                      <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Encrypted</span>
                      <span className="inline-flex items-center gap-1 text-emerald-700"><TrendingUp className="w-3 h-3" /> Active</span>
                    </div>
                    <div className="text-[10px] text-gray-400">
                      Updated {new Date(wallet.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

    </div>
  );
};

export default CardWallets;
